import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listPharmacies,
  listStock,
  lowStockSummary,
  downloadTemplate,
  bulkUpload,
  updateStockRow,
  deleteStockRow,
  setDrugImage,
} from '../../api/pharmacyStock.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import CatalogueImageThumb from '../../components/CatalogueImageThumb.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { useDebounce } from '../../hooks/useDebounce.js'
import { useAuth } from '../../auth/AuthProvider.jsx'
import AddDrugModal from './AddDrugModal.jsx'

/**
 * Pharmacy catalogue self-service page.
 *
 * Three sections:
 *   1. "Download template" — pull the formulary CSV (full or OTC-only) so
 *      the pharmacy can fill in price/stock/availability for the rows
 *      they actually stock and re-upload.
 *   2. "Upload" — drop in the edited CSV; server returns a per-row
 *      summary (created / updated / unmatched proposals / errors).
 *   3. "Current stock" — paginated list of every PharmacyStock row this
 *      pharmacy has, with inline edit on price / stock / availability
 *      and a delete button. Search-as-you-type on the drug name.
 *
 * Admins must pick a target pharmacy from the dropdown; pharmacy staff
 * are scoped to their own pharmacy automatically (server enforces).
 */
export default function PharmacyCatalogue() {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  // Pharmacy users are auto-scoped to their own pharmacy via the
  // OneToOne PharmacyStaffLink (exposed on /api/auth/me/ as
  // user.pharmacy_profile = { id, name, is_pharmacy_admin }).
  const linkedPharmacyId = user?.pharmacy_profile?.id ?? null
  const linkedPharmacyName = user?.pharmacy_profile?.name ?? ''

  // Admin can pick any pharmacy. Pharmacy staff see only their own.
  const pharmaciesQ = useQuery({
    queryKey: ['pharmacies-list'],
    queryFn: listPharmacies,
    enabled: isAdmin,
  })
  const [pharmacyId, setPharmacyId] = useState(linkedPharmacyId)
  // Default an admin's selection to the first pharmacy once the list
  // loads. Previously this `setPharmacyId(…)` was called directly during
  // render, which React's strict-mode flags and which (under some
  // network timings) bounced between an undefined and a defined value.
  // An effect run after commit is the correct shape.
  useEffect(() => {
    if (isAdmin && !pharmacyId && pharmaciesQ.data?.[0]?.id) {
      setPharmacyId(pharmaciesQ.data[0].id)
    }
  }, [isAdmin, pharmacyId, pharmaciesQ.data])

  // Show a friendly explainer if a pharmacy user has no link yet
  // (admin hasn't bound their account to a pharmacy via Django admin).
  if (!isAdmin && !linkedPharmacyId) {
    return (
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Catalogue self-service for pharmacy staff"
        />
        <div className="text-sm text-slate-600 bg-amber-50 border border-amber-200 rounded p-3">
          <b>Account not linked to a pharmacy yet.</b>
          <p className="mt-1">
            To manage your pharmacy's catalogue you need to be linked to a
            pharmacy by the platform administrator. Ask the admin to bind
            your user (<span className="font-mono">{user?.username}</span>)
            via Django admin → Pharmacy → Pharmacy staff links → Add.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="My catalogue"
          subtitle="Manage the drugs your pharmacy stocks: download the template, fill in price + stock, upload it back. Edit individual rows below."
          right={
            <div className="flex items-center gap-3">
              <a
                href="/pharmacy-catalogue-guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-wantok-700 hover:text-wantok-800 underline whitespace-nowrap"
              >
                Pharmacy user guide (PDF)
              </a>
              <a
                href="/pharmacy-sop.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-wantok-700 hover:text-wantok-800 underline whitespace-nowrap"
              >
                Pharmacy staff SOP (PDF)
              </a>
            </div>
          }
        />
        {isAdmin ? (
          <div className="mt-2">
            <label className="text-xs uppercase tracking-wide text-slate-500">
              Pharmacy
              <span className="ml-2 text-amber-700 normal-case font-normal">
                (admin — pick any)
              </span>
            </label>
            <select
              value={pharmacyId || ''}
              onChange={(e) => setPharmacyId(Number(e.target.value))}
              className="mt-1 w-full border rounded px-3 py-2 text-sm bg-white"
            >
              {(pharmaciesQ.data || []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-2 text-sm">
            <span className="text-[11px] uppercase tracking-wide text-slate-500 mr-2">Pharmacy</span>
            <span className="font-semibold text-slate-800">{linkedPharmacyName}</span>
            {user?.pharmacy_profile?.is_pharmacy_admin && (
              <span className="ml-2 text-[10px] uppercase border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-100">
                pharmacy admin
              </span>
            )}
          </div>
        )}
      </Card>

      {pharmacyId ? (
        <>
          <TemplateCard pharmacyId={pharmacyId} />
          <UploadCard pharmacyId={pharmacyId} />
          <StockListCard pharmacyId={pharmacyId} />
        </>
      ) : (
        <Card>
          <div className="text-sm text-slate-400 italic">
            {isAdmin
              ? 'Select a pharmacy above to manage its catalogue.'
              : 'Your account is not linked to a pharmacy. Ask the admin to link your user.'}
          </div>
        </Card>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Template download
// ---------------------------------------------------------------------------

function TemplateCard() {
  const [otcOnly, setOtcOnly] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState(null)

  async function handleDownload() {
    setError(null)
    setDownloading(true)
    try {
      const blob = await downloadTemplate({ otcOnly })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = otcOnly
        ? 'wantok_formulary_otc_template.csv'
        : 'wantok_formulary_template.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(e)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader title="1. Download template"
        subtitle="A CSV with every drug in the global formulary, identity columns pre-filled. Fill in price, stock_quantity, is_available, brand_name on the rows you stock; leave the rest blank." />
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm flex items-center gap-2">
          <input
            type="checkbox"
            checked={otcOnly}
            onChange={(e) => setOtcOnly(e.target.checked)}
          />
          OTC drugs only (~213 rows instead of ~1820)
        </label>
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
        >
          {downloading ? 'Preparing…' : 'Download CSV'}
        </button>
      </div>
      {error && <div className="mt-3"><ErrorBanner error={error} /></div>}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Upload + result summary
// ---------------------------------------------------------------------------

function UploadCard({ pharmacyId }) {
  const qc = useQueryClient()
  const fileRef = useRef(null)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)

  const mut = useMutation({
    mutationFn: (f) => bulkUpload({ file: f, pharmacyId }),
    onSuccess: (data) => {
      setResult(data)
      qc.invalidateQueries({ queryKey: ['pharmacy-stock', pharmacyId] })
      // Reset the picker so re-upload doesn't surprise the user.
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    },
  })

  return (
    <Card>
      <CardHeader title="2. Upload your filled-in CSV"
        subtitle="Multipart upload — only rows where you filled in price, stock, brand, or set is_available=true are processed. Re-uploading is safe (idempotent on (pharmacy, drug))." />
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm"
        />
        <button
          onClick={() => file && mut.mutate(file)}
          disabled={!file || mut.isPending}
          className="bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 text-sm disabled:opacity-60"
        >
          {mut.isPending ? 'Uploading…' : 'Upload'}
        </button>
        {file && <span className="text-xs text-slate-500">{file.name} · {Math.round(file.size / 1024)} KB</span>}
      </div>
      {mut.error && <div className="mt-3"><ErrorBanner error={mut.error} /></div>}
      {result && <UploadResult data={result} />}
    </Card>
  )
}

function UploadResult({ data }) {
  const s = data?.summary || {}
  const errors = data?.errors || []
  const proposals = data?.proposals || []
  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="Rows processed" value={s.rows_processed} />
        <Stat label="Created" value={s.stock_created} colour="emerald" />
        <Stat label="Updated" value={s.stock_updated} colour="sky" />
        <Stat label="Proposals" value={s.proposals_created} colour="amber" />
        <Stat label="Errors" value={s.errors} colour={s.errors ? 'rose' : 'slate'} />
      </div>
      {errors.length > 0 && (
        <div className="border border-rose-200 bg-rose-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-rose-700 mb-1">
            Row errors ({errors.length})
          </div>
          <ul className="text-xs text-rose-800 space-y-0.5 max-h-40 overflow-auto">
            {errors.map((e, i) => (
              <li key={i}><span className="font-mono">row {e.row}</span> · {e.error}</li>
            ))}
          </ul>
        </div>
      )}
      {proposals.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded p-2">
          <div className="text-[11px] uppercase tracking-wide text-amber-700 mb-1">
            Filed as drug proposals ({proposals.length}) — admin will review
          </div>
          <ul className="text-xs text-amber-900 space-y-0.5 max-h-40 overflow-auto">
            {proposals.map((p) => (
              <li key={p.proposal_id}>
                <span className="font-mono">row {p.row}</span> ·
                <b> {p.medication_name || '(no name)'}</b>
                {p.strength && <> {p.strength}</>}
                {p.dosage_form && <> ({p.dosage_form})</>}
                <span className="text-amber-600"> #{p.proposal_id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, colour = 'slate' }) {
  const map = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    sky: 'border-sky-200 bg-sky-50 text-sky-800',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    rose: 'border-rose-200 bg-rose-50 text-rose-800',
  }
  return (
    <div className={`border rounded px-2 py-1.5 text-center ${map[colour]}`}>
      <div className="text-lg font-semibold">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Existing stock — inline editor
// ---------------------------------------------------------------------------

function StockListCard({ pharmacyId }) {
  const { user } = useAuth()
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const debounced = useDebounce(search, 250)
  const stockQ = useQuery({
    queryKey: ['pharmacy-stock', pharmacyId, debounced],
    queryFn: () => listStock({ pharmacyId, search: debounced }),
    enabled: Boolean(pharmacyId),
  })

  const summaryQ = useQuery({
    queryKey: ['pharmacy-stock-summary', pharmacyId],
    queryFn: () => lowStockSummary({ pharmacyId }),
    enabled: Boolean(pharmacyId),
  })

  const rows = useMemo(() => stockQ.data || [], [stockQ.data])
  const filtering = Boolean(debounced?.trim())
  const subtitle = filtering
    ? `Showing ${rows.length} match${rows.length === 1 ? '' : 'es'} for "${debounced}". Clear the filter to see your full catalogue.`
    : `${rows.length} drug${rows.length === 1 ? '' : 's'} in your catalogue. Add drugs one at a time via + Add drug, or use CSV upload for bulk. Edit inline. Delete = you no longer stock it.`

  // "+ Add drug" refetches BOTH lists after a successful save. AddDrugModal
  // also invalidates via QueryClient — this belt-and-braces refetch is a
  // guard for the (rare) case the two query keys drift out of sync.
  function afterCreated() {
    stockQ.refetch()
    summaryQ.refetch()
  }

  return (
    <Card>
      <CardHeader title="3. Current stock"
        subtitle={subtitle}
        right={
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddModal(true)}
              className="text-xs px-2.5 py-1 rounded bg-wantok-700 hover:bg-wantok-800 text-white font-medium"
              title="Add one drug to your catalogue (search the global formulary)"
            >
              + Add drug
            </button>
            <button
              onClick={() => { stockQ.refetch(); summaryQ.refetch() }}
              disabled={stockQ.isFetching || summaryQ.isFetching}
              className="text-xs text-wantok-700 underline disabled:opacity-50"
            >
              {stockQ.isFetching || summaryQ.isFetching ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
      />
      <AddDrugModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={afterCreated}
      />
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter by drug name or brand…"
        className="w-full border rounded px-3 py-2 text-sm mb-3"
      />
      {summaryQ.data && (summaryQ.data.out > 0 || summaryQ.data.low > 0) && (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-xs rounded border border-amber-200 bg-amber-50 px-3 py-2">
          <span className="font-semibold text-amber-900">Stock alert:</span>
          {summaryQ.data.out > 0 && (
            <span className="inline-flex items-center rounded-full bg-rose-100 text-rose-800 px-2 py-0.5 font-semibold">
              {summaryQ.data.out} out of stock
            </span>
          )}
          {summaryQ.data.low > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 font-semibold">
              {summaryQ.data.low} low
            </span>
          )}
          <span className="text-amber-700">— set each row's reorder level to tune the Low threshold.</span>
        </div>
      )}
      {stockQ.error && <ErrorBanner error={stockQ.error} onRetry={() => stockQ.refetch()} />}
      {stockQ.isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400 italic">
          No stock rows yet. Upload a CSV above to populate, or add rows from
          the doctor-prescription flow.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
              <tr>
                <th className="p-2 w-12"></th>
                <th className="text-left p-2">Drug</th>
                <th className="text-left p-2">Brand</th>
                <th className="text-right p-2">Price (PGK)</th>
                <th className="text-right p-2">Stock</th>
                <th className="text-right p-2">Reorder at</th>
                <th className="text-center p-2">Available</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <StockRow
                  key={r.id}
                  row={r}
                  isAdmin={isAdmin}
                  onChanged={() => {
                    qc.invalidateQueries({ queryKey: ['pharmacy-stock', pharmacyId] })
                    qc.invalidateQueries({ queryKey: ['pharmacy-stock-summary', pharmacyId] })
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function StockRow({ row, isAdmin, onChanged }) {
  const [brand, setBrand] = useState(row.brand_name || '')
  const [price, setPrice] = useState(row.price ?? '')
  const [stock, setStock] = useState(row.stock_quantity ?? 0)
  const [reorder, setReorder] = useState(row.reorder_level ?? 0)
  const [avail, setAvail] = useState(row.is_available)

  // Re-sync local state when the server-side row updates (e.g. after a
  // CSV bulk upload invalidates the catalogue query). Otherwise useState
  // only reads row.* on mount and the inputs stay blank even though the
  // refetched row has the new values.
  useEffect(() => {
    setBrand(row.brand_name || '')
    setPrice(row.price ?? '')
    setStock(row.stock_quantity ?? 0)
    setReorder(row.reorder_level ?? 0)
    setAvail(row.is_available)
  }, [row.id, row.last_updated])

  const dirty = (
    brand !== (row.brand_name || '') ||
    String(price) !== String(row.price ?? '') ||
    Number(stock) !== Number(row.stock_quantity ?? 0) ||
    Number(reorder) !== Number(row.reorder_level ?? 0) ||
    avail !== row.is_available
  )

  const save = useMutation({
    mutationFn: () => updateStockRow(row.id, {
      brand_name: brand,
      price: price === '' ? null : price,
      stock_quantity: Number(stock) || 0,
      reorder_level: Number(reorder) || 0,
      is_available: Boolean(avail),
    }),
    onSuccess: onChanged,
  })

  const del = useMutation({
    mutationFn: () => deleteStockRow(row.id),
    onSuccess: onChanged,
  })

  const status = row.stock_status  // 'out' | 'low' | 'ok'
  const rowTone = dirty
    ? 'bg-amber-50/40'
    : status === 'out'
      ? 'bg-rose-50'
      : status === 'low'
        ? 'bg-amber-50/60'
        : ''

  return (
    <tr className={rowTone}>
      <td className="p-2">
        <CatalogueImageThumb
          url={row.drug_image_url}
          alt={row.drug_name}
          editable={isAdmin}
          onUpload={async (file) => {
            await setDrugImage(row.drug, file)
            onChanged()
          }}
          onClear={async () => {
            await setDrugImage(row.drug, null)
            onChanged()
          }}
        />
      </td>
      <td className="p-2">
        <div className="font-medium">{row.drug_name}</div>
        <div className="text-[11px] text-slate-500">
          {row.drug_strength} {row.drug_form && <span className="italic">{row.drug_form}</span>}
          {row.is_otc && <span className="ml-2 text-[10px] uppercase border rounded-full px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">OTC</span>}
        </div>
      </td>
      <td className="p-2">
        <input
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          className="w-full border rounded px-2 py-1 text-xs"
          placeholder="(generic)"
        />
      </td>
      <td className="p-2 text-right">
        <input
          type="number"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-24 border rounded px-2 py-1 text-xs text-right"
        />
      </td>
      <td className="p-2 text-right">
        <input
          type="number"
          value={stock}
          onChange={(e) => setStock(e.target.value)}
          className="w-20 border rounded px-2 py-1 text-xs text-right"
        />
        {status === 'out' && (
          <span className="ml-2 text-[10px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800">Out</span>
        )}
        {status === 'low' && (
          <span className="ml-2 text-[10px] font-bold uppercase rounded-full px-1.5 py-0.5 bg-amber-100 text-amber-900">Low</span>
        )}
      </td>
      <td className="p-2 text-right">
        <input
          type="number"
          min="0"
          value={reorder}
          onChange={(e) => setReorder(e.target.value)}
          title="Low-stock threshold in single units. 0 = no Low warning."
          className="w-16 border rounded px-2 py-1 text-xs text-right"
        />
      </td>
      <td className="p-2 text-center">
        <input
          type="checkbox"
          checked={Boolean(avail)}
          onChange={(e) => setAvail(e.target.checked)}
        />
      </td>
      <td className="p-2 text-right whitespace-nowrap">
        <button
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="text-xs bg-wantok-700 hover:bg-wantok-800 text-white rounded px-2 py-1 disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Stop stocking ${row.drug_name}?`)) del.mutate()
          }}
          disabled={del.isPending}
          className="ml-2 text-xs text-rose-600 hover:underline disabled:opacity-40"
        >
          Delete
        </button>
      </td>
    </tr>
  )
}
